import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Button, 
  Box, 
  CircularProgress, 
  Alert,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton,
  Tooltip,
  useTheme,
  InputBase,
  Dialog,
  DialogTitle,
  DialogContent,
  alpha
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '../context/AuthContext';
import ReactMarkdown from 'react-markdown';


function NewsDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { authAxios } = useAuth();
  const [newsItems, setNewsItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  // Function to fetch news
  const fetchNews = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authAxios.get('/news');
      setNewsItems(response.data);
      setFilteredItems(response.data);
    } catch (err) {
      console.error("Failed to fetch news:", err);
      setError(
        err.response?.data?.message || 
        err.message || 
        'Failed to load news items.'
      );
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  // Filter news items based on search query (bilingual-safe)
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredItems(newsItems);
    } else {
      const lower = searchQuery.toLowerCase();
      const filtered = newsItems.filter(item => {
        const title = item.title_en || item.title_de || '';
        const body  = item.body_en  || item.body_de  || '';
        return (
          title.toLowerCase().includes(lower) ||
          body.toLowerCase().includes(lower)
        );
      });
      setFilteredItems(filtered);
    }
  }, [searchQuery, newsItems]);

  // Fetch news on component mount
  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Navigation handlers
  const handleAddNews = () => {
    navigate('/news/add');
  };

  const handleEditNews = (id) => {
    navigate(`/news/edit/${id}`);
  };

  // Open preview dialog with selected item
  const handleViewNews = (item) => {
    setPreviewItem(item);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewItem(null);
  };

  const handleDeleteNews = async (id) => {
    if (window.confirm('Are you sure you want to delete this news item?')) {
      setError('');
      try {
        await authAxios.delete(`/news/${id}`);
        fetchNews(); 
      } catch (err) {
        console.error("Failed to delete news:", err);
        setError(
          err.response?.data?.message || 
          err.message || 
          'Failed to delete news item.'
        );
      }
    }
  };

  return (
    <Box 
      sx={{ 
        width: '100%',
        m: 0,
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 4 },
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          News List
        </Typography>
      </Box>

      {/* Action Bar */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        flexWrap: 'wrap',
        gap: 2,
        mb: 3 
      }}>
        {/* Search Bar */}
        <Paper
          component="form"
          onSubmit={(e) => e.preventDefault()}
          sx={{
            p: '2px 4px',
            display: 'flex',
            alignItems: 'center',
            width: { xs: '100%', sm: 320 },
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none'
          }}
        >
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Search news items"
            inputProps={{ 'aria-label': 'search news items' }}
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
            <SearchIcon />
          </IconButton>
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={handleAddNews}
            sx={{ 
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)'
            }}
          >
            Add News
          </Button>
        </Box>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading news items...
          </Typography>
        </Box>
      )}

      {/* Error */}
      {!loading && error && (
        <Alert 
          severity="error" 
          sx={{ mt: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}
        >
          <Box sx={{ flex: 1 }}>{error}</Box>
          <Button variant="outlined" size="small" onClick={fetchNews}>
            Retry
          </Button>
        </Alert>
      )}
      
      {/* Content */}
      {!loading && !error && (
        <>
          {filteredItems.length === 0 ? (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No news items found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchQuery 
                  ? 'Try a different search term or clear the search.' 
                  : 'Get started by adding a new news item.'}
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={handleAddNews}
                sx={{ mt: 2, borderRadius: 2 }}
              >
                Add News
              </Button>
            </Paper>
          ) : (
            <TableContainer 
              component={Paper} 
              elevation={0}
              sx={{ 
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                overflow: 'hidden',
                mt: 1
              }}
            >
              <Table sx={{ minWidth: 650 }} size="small" aria-label="news table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Image</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Content Preview</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>D/P</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredItems.map((item) => (
                    <TableRow
                      key={item.id}
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: alpha(theme.palette.primary.main, 0.02) 
                        }
                      }}
                    >
                      <TableCell>
                        {item.image_url ? (
                          <Box 
                            component="img" 
                            src={item.image_url} 
                            alt={item.title_en || item.title_de || 'Untitled'}
                            sx={{ 
                              width: 100, 
                              height: 60, 
                              objectFit: 'contain',
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.divider}`
                            }} 
                          />
                        ) : (
                          <Box 
                            sx={{ 
                              width: 100, 
                              height: 60, 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                              borderRadius: 1,
                              color: 'text.secondary',
                              fontSize: '0.75rem'
                            }}
                          >
                            No Image
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {item.title_en || item.title_de || 'Untitled'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {(() => {
                            const content = item.body_en || item.body_de || '';
                            const preview = content.substring(0, 100);
                            return content.length > 100 ? `${preview}...` : preview;
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
  
                      <Typography
                        variant="caption"
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontWeight: 'bold',
                          color: item.status === 'published' ? 'success.main' : 'warning.main',
                          backgroundColor: item.status === 'published'
                            ? alpha(theme.palette.success.main, 0.15)
                            : alpha(theme.palette.warning.main, 0.15),
                          textTransform: 'capitalize',
                        }}
                      >
                        {item.status}
                      </Typography>

                      </TableCell>
                      <TableCell align="right" width={140}>
                        <Tooltip title="View">
                          <IconButton 
                            size="small"
                            onClick={() => handleViewNews(item)}
                            sx={{ 
                              color: theme.palette.info.main,
                              backgroundColor: alpha(theme.palette.info.main, 0.1),
                              mr: 1,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.info.main, 0.2),
                              }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small"
                            onClick={() => handleEditNews(item.id)}
                            sx={{ 
                              color: theme.palette.primary.main,
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              mr: 1,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small"
                            onClick={() => handleDeleteNews(item.id)}
                            sx={{ 
                              color: theme.palette.error.main,
                              backgroundColor: alpha(theme.palette.error.main, 0.1),
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.2),
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Preview dialog – shows card like on homepage */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Homepage Preview</DialogTitle>
        <DialogContent>
          {previewItem && (
            <Box
  sx={{
    position: 'relative',
    width: '100%',
    maxWidth: 350,
    height: 300,
    borderRadius: 2,
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
    mx: 'auto'
  }}
>
        <Box
          component="img"
          src={previewItem.image_url}
          alt={previewItem.title_en || previewItem.title_de || 'Untitled'}
          sx={{
          objectFit: 'contain',
          width: '100%',
          height: '100%',
          }}
        />

    <Box
  sx={{
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.0))',
    display: 'flex',
    alignItems: 'flex-end',
    p: 2,
    color: 'white',
  }}
>
      <Box>
         <Typography
      variant="subtitle1"
      sx={{
        fontWeight: 700,
        color: 'white',
        mb: 0.1,
        fontSize: '1.2rem',
        lineHeight: 1.2,
      }}
    >
      {previewItem.title_en || previewItem.title_de || "Untitled"}
    </Typography>

        <Typography
          variant="body2"
          sx={{
            mb: 1,
            fontSize: '0.8rem',
            color: 'white',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 6,
            WebkitBoxOrient: 'vertical',
          }}
        >
          <ReactMarkdown
            children={
              (() => {
                const content = previewItem.body_en || previewItem.body_de || '';
                const preview = content.slice(0, 250);
                return content.length > 250 ? preview + '...' : preview;
              })()
            }
          />
        </Typography>


        <Button
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: 1,
              backgroundColor: "secondary.main",
              boxShadow: '0px 10px 25px rgba(255, 255, 255, 0.62)',
              "&:hover": { backgroundColor: "primary.primary" },
              fontSize: "0.75rem", 
              py: "0.8px",           
              px: "3.5px",          
              width: "85px",      
            }}
            onClick={() => navigate(`/news/${previewItem.id}`)}
          >
            Read More
        </Button>
            </Box>
          </Box>
        </Box>

          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default NewsDashboard;
